/**
 * How a customer pays, once they have signed.
 *
 * The page that replaces "All done" offers three things and this module decides
 * all three: what they cost, what they are called, and which of them are on
 * offer at all. None of it lives in the component (CLAUDE.md rule 5) — the
 * numbers on that page are the numbers a customer is charged.
 *
 * ## The 3%
 *
 * Jonathon, 2026-09-03 and again on 2026-09-06: a card costs the customer 3%
 * and bank transfer costs them nothing. This reverses the "no surcharge"
 * decision in ADR 0063, which was taken when the company absorbed Stripe's
 * 2.9% + 30c and answered it with a $5,000 cap on cards. The cap is gone with
 * it: a card the customer pays for is a card the company can take on any size
 * of job.
 *
 * The surcharge is charged on **the payment, not the contract**. Half of a
 * $20,000 roof on a card is a $300 fee, and if the balance later arrives by
 * bank transfer there is no second one. That is his instruction in his own
 * words — "only needs to be for the actual amount charged and paid by the
 * customer" — and it is why `cardSurcharge` takes an amount rather than a job.
 *
 * ## Why "debit" is not in this file
 *
 * Surcharging a debit card is illegal nationwide, and a debit card costs the
 * company the same 2.9% + 30c as a credit card. So the word appears nowhere a
 * customer can read: offering it would be offering something we cannot charge
 * for. A customer who types a debit card into the card option is taken, and the
 * company pays that one. Steering, not policing — his decision, 2026-09-06.
 * `packages/core/test/paymentChoice.test.ts` holds the line by searching every
 * piece of copy in here for the word.
 */

import { formatMoney, type Cents, cents } from "./money.js";

/** What the customer pays on top for using a credit card. */
export const CARD_SURCHARGE_RATE = 0.03;

/** How the fee is described wherever it is shown. Never "debit". */
export const CARD_SURCHARGE_LABEL = "Credit card convenience fee (3%)";

/**
 * The fee on an amount, rounded to the cent.
 *
 * `Math.round` goes up on a half cent, the company's way everywhere else in
 * this package. Zero or less is charged nothing rather than credited: a
 * negative fee is not a thing that can happen and would be worth noticing
 * rather than quietly booking.
 */
export function cardSurcharge(amount: Cents): Cents {
  if (amount <= 0) return 0 as Cents;
  return Math.round(amount * CARD_SURCHARGE_RATE) as Cents;
}

/** What a card payment of `amount` actually charges the card. */
export function withCardSurcharge(amount: Cents): Cents {
  return cents(amount + cardSurcharge(amount));
}

/** The three ways to pay, in the order the page shows them. */
export type PaymentMethod = "financing" | "card" | "ach";

export interface PaymentChoiceCard {
  method: PaymentMethod;
  /** The heading on the card. */
  title: string;
  /** One line under it. */
  blurb: string;
  /** What the customer is charged if they pick this. Null for financing. */
  amount: Cents | null;
  amountFormatted: string | null;
  /** The fee inside that amount, when there is one. */
  fee: Cents;
  feeFormatted: string | null;
  /** The words on the button. */
  action: string;
}

export interface PaymentChoiceInput {
  /** The payment being asked for, before any fee. */
  due: Cents;
  /** The whole contract, for the sentence above the cards. */
  contract: Cents;
  /**
   * Whether that payment is actually owed today.
   *
   * False on a job under $1,000, where the schedule takes the whole thing at
   * completion. Jonathon still wants the cards there — "i'm fine with if a
   * customer wants to proactively pay for something" — so the difference is
   * one sentence, not a different page.
   */
  dueNow?: boolean;
  /**
   * Which payment this is: 1 is the deposit at signing, 4 the balance after the
   * work is finished. It changes what the page calls the money and nothing
   * else — a customer paying the balance is not making a "first payment".
   */
  slot?: number | undefined;
  /** The lender's application page. No link, no financing card. */
  financingUrl?: string | null;
  /**
   * What the customer has just put their name to.
   *
   * It changes the words and one rule, and nothing else. A change order is
   * signed in the middle of a job: the money is a balance rather than a first
   * payment, nobody has finished the work yet, and financing is not on offer
   * — a lender will not underwrite a roof that is half on, and the customer
   * has already been through that decision once.
   *
   * Jonathon, 2026-09-22, asked what the page should ask for after a change
   * order is signed: everything still owed on the job. So the sentence names
   * the whole balance, not the change.
   */
  purpose?: "contract" | "change-order" | undefined;
}

export interface PaymentChoices {
  /** The heading. */
  heading: string;
  /** The sentence between the heading and the cards. */
  intro: string;
  cards: readonly PaymentChoiceCard[];
}

/**
 * What to put on the page.
 *
 * A payment of zero — a job under $1,000 whose rep set the first payment to
 * nothing, or a contract we cannot price — still gets a page, because the
 * customer has just signed and being thanked matters. It gets the financing
 * card and no amounts: there is nothing to charge, and inventing a figure to
 * fill the space would be worse than the gap.
 */
export function paymentChoices(input: PaymentChoiceInput): PaymentChoices {
  const due = cents(Math.max(0, input.due));
  const fee = cardSurcharge(due);
  const isChangeOrder = input.purpose === "change-order";
  // A change order's ask is the whole outstanding balance, so it is a balance
  // in every way that matters here — the wording, and the financing card that
  // a balance never gets.
  const isBalance = input.slot === 4 || isChangeOrder;
  /*
   * No financing on the balance, whatever the setting says.
   *
   * Jonathon, 2026-09-06: "that final link doesn't need to mention financing."
   * It is arranged before the work, not after it — offering it beside a
   * finished roof invites a customer to go and apply rather than pay, and a
   * lender will not underwrite work that is already done.
   *
   * The server withholds the link on a balance too. This is the second lock,
   * and it is the one that holds if a caller ever passes the URL in anyway.
   */
  const financingUrl = isBalance ? "" : (input.financingUrl ?? "").trim();

  const cards: PaymentChoiceCard[] = [];

  if (financingUrl) {
    cards.push({
      method: "financing",
      title: "Apply for financing",
      blurb:
        "Spread the cost over monthly payments. Checking your rate takes a " +
        "couple of minutes and does not affect your credit score.",
      amount: null,
      amountFormatted: null,
      fee: 0 as Cents,
      feeFormatted: null,
      action: "Check my rate",
    });
  }

  if (due > 0) {
    cards.push({
      method: "card",
      title: "Pay by credit card",
      blurb:
        `Card payments carry a 3% convenience fee of ${formatMoney(fee)}, ` +
        "which is already included in the amount shown.",
      amount: withCardSurcharge(due),
      amountFormatted: formatMoney(withCardSurcharge(due)),
      fee,
      feeFormatted: formatMoney(fee),
      action: "Pay by card",
    });

    cards.push({
      method: "ach",
      title: "Pay by bank transfer",
      blurb:
        "No fee. You will be asked for your bank details on a secure page, " +
        "and the payment usually clears in one to three business days.",
      amount: due,
      amountFormatted: formatMoney(due),
      fee: 0 as Cents,
      feeFormatted: null,
      // Not "Pay by bank transfer", which is the heading immediately above it.
      // The button says what pressing it does next, and pressing it opens the
      // bank's page rather than taking the money there and then.
      action: "Continue with bank transfer",
    });
  }

  const dueNow = input.dueNow ?? true;

  return {
    heading: isChangeOrder
      ? "Thank you — your change order is approved"
      : isBalance
        ? "Your work is complete"
        : "Thank you — your contract is signed",
    intro:
      due <= 0
        ? isChangeOrder
          ? "There is nothing to pay right now. We will carry on with your " +
            "work and be in touch."
          : "We will be in touch to book your work in."
        : isChangeOrder
          ? `That leaves a balance of ${formatMoney(due)} on your job. You ` +
            "can pay it now if you would like to, or we will ask for it when " +
            "the work is finished."
          : isBalance
            ? `That leaves a balance of ${formatMoney(due)}. Choose how you ` +
              "would like to pay it."
            : dueNow
              ? `Your first payment of ${formatMoney(due)} is due now. ` +
                "Choose how you would like to pay it."
              : `You can pay your ${formatMoney(due)} balance now if you ` +
                "would like to, or leave it until the work is finished.",
    cards,
  };
}

/**
 * What a chosen method charges, and what of that is fee.
 *
 * The one place the amount on a Mercury invoice is decided, so the page and the
 * invoice cannot disagree about what somebody owes.
 */
export function chargeFor(
  method: PaymentMethod,
  due: Cents,
): { total: Cents; surcharge: Cents } {
  if (method !== "card") return { total: cents(due), surcharge: 0 as Cents };
  const surcharge = cardSurcharge(due);
  return { total: cents(due + surcharge), surcharge };
}

/**
 * Whether Roof Board asks this job's customer to pay at all: the buttons on
 * the pay page, the letter at signing, the invoice and the reminders.
 *
 * `mercury_settings.enabled` is the switch for real customers, and it has been
 * off since 2026-09-10 while the pay link was mended (#202, #204). A job marked
 * Test is asked whichever way it is set, so the whole flow — sign, choose, pay
 * a real $1 through Mercury — can be tried with no customer in it (Josh,
 * 2026-09-11, ADR 0129). A test job's customer is somebody on staff.
 */
export function paymentsOpen(input: {
  switchedOn: boolean;
  isTest: boolean | null | undefined;
}): boolean {
  return input.switchedOn || input.isTest === true;
}
